import React, { useState, useEffect } from 'react';
import { Plus, Users, FileText, Receipt, RotateCcw, Truck, CreditCard, FileCheck } from 'lucide-react';
import { cn } from '../../../utils/cn';

const SalesActionDropdown = ({ onAction }) => {
  const [isOpen, setIsOpen] = useState(false);

  const actionOptions = [
    {
      value: 'add-customer',
      label: 'Add Customer',
      icon: Users,
      description: 'Create a new customer profile'
    },
    {
      value: 'create-quote',
      label: 'Create Quote',
      icon: FileText,
      description: 'Generate a new quotation'
    },
    {
      value: 'create-retainer',
      label: 'Create Retainer',
      icon: CreditCard,
      description: 'Set up a retainer agreement'
    },
    {
      value: 'create-invoice',
      label: 'Create Invoice',
      icon: Receipt,
      description: 'Generate a new invoice'
    },
    {
      value: 'record-payment',
      label: 'Record Payment',
      icon: CreditCard,
      description: 'Record payment received from customer'
    },
    {
      value: 'setup-recurring',
      label: 'Setup Recurring',
      icon: RotateCcw,
      description: 'Configure recurring invoices'
    },
    {
      value: 'create-challan',
      label: 'Create Challan',
      icon: Truck,
      description: 'Create delivery challan'
    },
    {
      value: 'create-credit-note',
      label: 'Create Credit Note',
      icon: FileCheck,
      description: 'Issue a credit note to customer'
    }
  ];

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionSelect = (option) => {
    onAction?.(option?.value);
    setIsOpen(false);
  };

  const handleClickOutside = (e) => {
    if (!e?.target?.closest('.sales-action-dropdown')) {
      setIsOpen(false);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.removeEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="relative sales-action-dropdown">
      <button
        type="button"
        className={cn(
          "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          "bg-primary text-primary-foreground hover:bg-primary/90",
          "h-10 px-4 py-2 gap-2"
        )}
        onClick={handleToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Plus className="h-4 w-4" />
        New
        <svg
          className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 z-50 mt-1 w-64 bg-white border border-border rounded-md shadow-md">
          <div className="py-1">
            {actionOptions?.map((option) => {
              const IconComponent = option?.icon;
              return (
                <button
                  key={option?.value}
                  type="button"
                  className="relative flex w-full cursor-pointer select-none items-start rounded-sm px-3 py-3 text-sm outline-none hover:bg-accent hover:text-accent-foreground transition-colors"
                  onClick={() => handleOptionSelect(option)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className="flex-shrink-0">
                      <IconComponent className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-foreground">
                        {option?.label}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {option?.description}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesActionDropdown;